import React, { FC, useState } from "react";
import clsx from "clsx";
import { FiCheck } from "react-icons/fi";

import { AdventDoor } from "apps/user/learner/api/types";
import {
  CATEGORY_ICONS,
  DoorState,
  formatDoorDate,
} from "apps/user/learner/lib/advent-doors";
import { useToast } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";

import styles from "../december-doors.module.scss";

const SPARK_COUNT = 8;

interface DoorTileProps {
  door: AdventDoor;
  state: DoorState;
  isToday: boolean;
  /** The open mutation is in flight for this door: the panel is hinging. */
  isOpening: boolean;
  /** The door has just opened: play the spark burst once. */
  isSparkling: boolean;
  onOpen: (door: AdventDoor) => void;
}

const DoorTile: FC<DoorTileProps> = ({
  door,
  state,
  isToday,
  isOpening,
  isSparkling,
  onOpen,
}) => {
  const { t, i18n } = useTranslation(["decemberDoors"]);
  const { openToast } = useToast();
  const [isWobbling, setIsWobbling] = useState(false);

  const date = formatDoorDate(door.date, i18n.language);
  const isOpened = state === DoorState.Opened;
  const isLocked = state === DoorState.Locked;
  const isCompleted = isOpened && door.isCompleted;
  const Icon = CATEGORY_ICONS[door.category];

  const handleClick = () => {
    if (isLocked) {
      setIsWobbling(true);
      openToast({
        messageHeader: t("decemberDoors:locked.title"),
        message: t("decemberDoors:locked.hint", { date }),
        className: styles.toast,
        size: "medium",
      });
      return;
    }
    onOpen(door);
  };

  const a11yLabel = () => {
    if (isOpened) {
      const opened = t("decemberDoors:door.a11yOpened", {
        day: door.dayNumber,
        title: door.title,
      });
      return isCompleted
        ? `${opened}. ${t("decemberDoors:mechanic.done")}`
        : opened;
    }
    if (isLocked) {
      return t("decemberDoors:door.a11yLocked", { day: door.dayNumber, date });
    }
    if (isToday) {
      return t("decemberDoors:door.a11yToday", { day: door.dayNumber });
    }
    return t("decemberDoors:door.a11yAvailable", { day: door.dayNumber });
  };

  return (
    <button
      type="button"
      className={clsx(
        styles.door,
        styles[`door-${state}`],
        isToday && !isOpened && styles["door-today"],
        isOpening && styles["door-opening"],
        isWobbling && styles["door-wobble"]
      )}
      onClick={handleClick}
      onAnimationEnd={() => setIsWobbling(false)}
      aria-label={a11yLabel()}
      data-testid={`door-${door.dayNumber}`}
      data-state={state}
      data-completed={isCompleted || undefined}
      data-today={isToday || undefined}
    >
      {isOpened ? (
        <span className={styles["door-inside"]} aria-hidden>
          <span className={styles["door-inside-number"]}>{door.dayNumber}</span>
          <Icon className={styles["door-inside-icon"]} />
          {isCompleted && (
            <span
              className={styles["door-completed-mark"]}
              data-testid={`door-${door.dayNumber}-completed`}
            >
              <FiCheck />
            </span>
          )}
        </span>
      ) : (
        <>
          <span className={styles["door-inside"]} aria-hidden>
            <Icon className={styles["door-inside-icon"]} />
          </span>
          <span className={styles["door-face"]} aria-hidden>
            <span className={styles["door-number"]}>{door.dayNumber}</span>
          </span>
        </>
      )}
      {isToday && !isOpened && (
        <span className={styles["door-today-pill"]} aria-hidden>
          {t("decemberDoors:door.today")}
        </span>
      )}
      {isSparkling && (
        <span className={styles["door-sparks"]} aria-hidden>
          {Array.from({ length: SPARK_COUNT }, (_, index) => (
            <span
              key={index}
              className={styles["door-spark"]}
              style={
                {
                  "--spark-angle": `${(360 / SPARK_COUNT) * index}deg`,
                } as React.CSSProperties
              }
            />
          ))}
        </span>
      )}
    </button>
  );
};

export default DoorTile;
